'use client';

import { useMemo, useState, type ComponentProps } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, SingleSelect } from '@galapagos/design-system/web';
import type { SignupProgramOption } from '@galapagos/shared/registration';
import { FormActions } from '../components/FormActions';
import { PrivacyNote } from '../components/PrivacyNote';
import { useCatalogWords } from '../hooks/useCatalogWords';
import {
  isCompleteProgramSelection,
  keptProgramSelection,
  pickSingleProgram,
  pickSingleSubOption,
  singleProgramSelection,
  wordedProgramOptions,
} from '../services/program-options';
import { useSignupFlowStore } from '../stores/signup-flow-store';

type SelectOptions = ComponentProps<typeof SingleSelect>['options'];

/**
 * The substance step's choice for a sponsor that allows one program at a
 * time: one program from the list and, while that program has sub-types, one
 * sub-type from a second list under it. The selection keeps the shape the
 * multi-select stores (`[programKey]` or `[programKey, subOptionValue]`).
 */
export function SingleProgramChoice({
  options,
  repick,
  onContinue,
}: {
  options: readonly SignupProgramOption[];
  /** Choosing again after every earlier choice was too young: start empty. */
  repick: boolean;
  onContinue: (selection: string[]) => void;
}) {
  const { t } = useTranslation('signup');
  const storedSelection = useSignupFlowStore((state) => state.programSelection);
  const stored = repick ? null : storedSelection;
  const [selection, setSelection] = useState(() =>
    singleProgramSelection(options, keptProgramSelection(options, stored)),
  );
  const words = useCatalogWords();
  const worded = useMemo(() => wordedProgramOptions(words, options), [words, options]);
  const programOptions: SelectOptions = useMemo(
    () =>
      worded.map((option) => ({
        value: option.programKey,
        label: option.label,
        ...(option.description
          ? {
              description: option.description,
              descriptionReveal: 'info-dot' as const,
              infoDotAccessibilityLabel: t('substance.infoDotLabel', { option: option.label }),
            }
          : {}),
      })),
    [worded, t],
  );
  const [programKey = null, subValue = null] = selection;
  const chosen = worded.find((option) => option.programKey === programKey);
  const complete = isCompleteProgramSelection(options, selection);
  return (
    <>
      {/* The choices are health information: ph-no-capture keeps PostHog's
          autocapture (label text, test ids, rage clicks) away from them. */}
      <div className="signup-step__group ph-no-capture" data-testid="signup-programs-capture-guard">
        <SingleSelect
          actionId="signup.substance-screen-program.change"
          infoDotActionId="signup.substance-screen-program-info.toggle"
          label={t('substance.optionsLabel')}
          hideLabel
          options={programOptions}
          value={programKey}
          onSelect={(value) => setSelection((current) => pickSingleProgram(current, value))}
          data-testid="signup-programs"
        />
        {chosen?.subOptions?.length ? (
          <SingleSelect
            actionId="signup.substance-screen-program-type.change"
            label={chosen.subOptionsLabel ?? chosen.label}
            options={chosen.subOptions.map((sub) => ({ value: sub.value, label: sub.label }))}
            value={subValue}
            onSelect={(value) =>
              setSelection((current) => pickSingleSubOption(options, current, value))
            }
            data-testid="signup-program-types"
          />
        ) : null}
        {chosen?.selectedNote ? (
          <p className="signup-selected-note">{chosen.selectedNote}</p>
        ) : null}
      </div>
      <PrivacyNote text={t('substance.privacyNote')} />
      <FormActions>
        <Button
          size="L"
          type="Primary"
          label={t('actions.continue')}
          state={complete ? 'Default' : 'Disabled'}
          onPress={() => onContinue(selection)}
          actionId="signup.substance-screen-continue.activate"
          data-testid="signup-continue"
        />
      </FormActions>
    </>
  );
}
